/**
 * Overview Section Switch
 * 
 * Control: Current implementation (legacy components)
 * Variation: New design using ContentSectionTemplate
 */

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Box } from '@mui/material';
import { InView } from 'react-intersection-observer';
import { getTenantById, isTenantExpCA, isZoocasaTenant, TenantId } from '@tenants';
import { AllowedExperimentKey } from '@tenants/types';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_VIEW_ADDRESS_PAGE_KEY_FACTS_TAB } from 'constants/events';
import { LOCALE_COMMON_PATHS, LOCALE_LISTING_PATHS, Namespaces } from 'types/locales';
import { ExperimentalSection } from 'ui/experimental-section';
import { ContentSectionTemplate, ContentSubSection } from 'components/atomic/templates/content-section';
import {
  Description,
  KeyFacts,
  PropertyDetails,
  Rooms,
  resolveDisplayRooms,
} from 'components/pages/address/public/components';
import type { ListingDetails } from 'components/pages/address/public/components/key-facts';
import type Listing from 'data/listing';
import type { Room } from 'data/listing';

// ============================================================================
// Types
// ============================================================================

export interface OverviewSectionProps {
  /** Section ID for scroll targeting */
  id: string;
  /** Tenant ID for localization context */
  tenantId: TenantId;
  /** Key facts data */
  keyFacts: ListingDetails;
  /** Full address string */
  fullAddress: string;
  /** Listing agent info */
  listingAgent: {
    expAgentId?: number;
    showMilitaryDetails?: boolean;
  };
  /** Listing data for description */
  listing: Listing;
  /** Whether content should be blurred/private */
  isPrivate: boolean;
  /** Whether to scramble content */
  shouldScramble: boolean;
  /** Whether to show walk score */
  showWalkScore: boolean;
  /** Callback when rooms detail is clicked */
  onRoomsShowDetailsClick?: () => void;
  /** Whether to hide the square footage */
  hideSquareFootage?: boolean;
  /** When false, key facts omit the property address row */
  showPropertyAddress?: boolean;
}

// ============================================================================
// Shared Hook
// ============================================================================

function useOverviewTracking() {
  return useCallback((isVisible: boolean) => {
    AnalyticsController.trackEventConditionally(
      { event: GTM_VIEW_ADDRESS_PAGE_KEY_FACTS_TAB },
      isVisible
    );
  }, []);
}

// ============================================================================
// Control (Current Implementation)
// ============================================================================

function OverviewSectionControl({
  id,
  tenantId,
  keyFacts,
  fullAddress,
  listingAgent,
  listing,
  isPrivate,
  shouldScramble,
  showWalkScore,
  onRoomsShowDetailsClick,
  hideSquareFootage,
  showPropertyAddress = true,
}: OverviewSectionProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const trackKeyFactsView = useOverviewTracking();

  const maintenanceFeesContext =
    isTenantExpCA(tenantId) || isZoocasaTenant(tenantId) ? 'ca' : 'us';

  return (
    <Box component="section" id={id} sx={{ scrollMarginTop: 'var(--scroll-offset, 150px)' }}>
      <InView onChange={trackKeyFactsView} triggerOnce>
        <KeyFacts
          listingDetails={keyFacts}
          isPrivate={isPrivate}
          shouldScramble={shouldScramble}
          address={fullAddress}
          localizedMaintenanceFeesLabel={t(
            LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_MAINTENANCE_FEES,
            { context: maintenanceFeesContext }
          )}
          listingAgent={listingAgent}
          hideSquareFootage={hideSquareFootage}
          showPropertyAddress={showPropertyAddress}
        />
      </InView>
      <Description
        listing={listing}
        isPrivate={isPrivate}
        shouldScramble={shouldScramble}
        showWalkScore={showWalkScore}
      />
      {listing.rooms && listing.rooms.length > 0 && (
        <Rooms
          rooms={listing.rooms as Room[]}
          isPrivate={isPrivate}
          shouldScramble={shouldScramble}
          onShowDetailsClick={onRoomsShowDetailsClick}
        />
      )}
    </Box>
  );
}

// ============================================================================
// Variation (New Design with Templates)
// ============================================================================

function OverviewSectionVariation({
  id,
  tenantId,
  keyFacts,
  fullAddress,
  listingAgent,
  listing,
  isPrivate,
  shouldScramble,
  showWalkScore,
  onRoomsShowDetailsClick,
  hideSquareFootage,
  showPropertyAddress = true,
}: OverviewSectionProps) {
  const { t } = useTranslation([Namespaces.COMMON, Namespaces.LISTING]);
  const trackKeyFactsView = useOverviewTracking();

  const maintenanceFeesContext =
    isTenantExpCA(tenantId) || isZoocasaTenant(tenantId) ? 'ca' : 'us';
  const localizedMaintenanceFeesLabel = t(
    LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_MAINTENANCE_FEES,
    { context: maintenanceFeesContext }
  );

  // The redesign merges Description + Key Facts + Additional Information into a single
  // "Property Details" block. Keep it in sync with the tab-based control layout so the
  // feature shows regardless of which STICKY_HEADER_TABS arm renders.
  const showPropertyDetailsRedesign = !!getTenantById(tenantId).features.ListingPropertyDetailsRedesign;
  const roomsToDisplay = useMemo(
    () => resolveDisplayRooms(listing, showPropertyDetailsRedesign),
    [listing, showPropertyDetailsRedesign]
  );

  const roomsSubSection = roomsToDisplay && roomsToDisplay.length > 0 ? (
    <ContentSubSection heading={t(LOCALE_COMMON_PATHS.ADDRESS_ROOMS_TAB_LABEL)}>
      <Rooms
        rooms={roomsToDisplay}
        isPrivate={isPrivate}
        shouldScramble={shouldScramble}
        onShowDetailsClick={onRoomsShowDetailsClick}
        variant={showPropertyDetailsRedesign ? 'table' : 'grid'}
      />
    </ContentSubSection>
  ) : null;

  if (showPropertyDetailsRedesign) {
    return (
      <ContentSectionTemplate
        id={id}
        heading={t(LOCALE_COMMON_PATHS.ADDRESS_PROPERTY_DETAILS_TAB_LABEL, { defaultValue: 'Property Details' })}
        withCard={false}
      >
        <InView onChange={trackKeyFactsView} triggerOnce>
          <PropertyDetails
            listing={listing}
            listingDetails={keyFacts}
            address={fullAddress}
            isPrivate={isPrivate}
            shouldScramble={shouldScramble}
            showWalkScore={showWalkScore}
            localizedMaintenanceFeesLabel={localizedMaintenanceFeesLabel}
            listingAgent={listingAgent}
            hideSquareFootage={hideSquareFootage}
            showPropertyAddress={showPropertyAddress}
          />
        </InView>
        {roomsSubSection}
      </ContentSectionTemplate>
    );
  }

  return (
    <ContentSectionTemplate id={id} heading={t(LOCALE_LISTING_PATHS.TAB_OVERVIEW)} withCard={false}>
      <InView onChange={trackKeyFactsView} triggerOnce>
        <ContentSubSection heading={t(LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_TAB_LABEL)}>
          <KeyFacts
            listingDetails={keyFacts}
            isPrivate={isPrivate}
            shouldScramble={shouldScramble}
            address={fullAddress}
            localizedMaintenanceFeesLabel={localizedMaintenanceFeesLabel}
            listingAgent={listingAgent}
            hideSquareFootage={hideSquareFootage}
            showPropertyAddress={showPropertyAddress}
          />
        </ContentSubSection>
      </InView>

      <ContentSubSection heading={t(LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_TAB_LABEL)}>
        <Description
          listing={listing}
          isPrivate={isPrivate}
          shouldScramble={shouldScramble}
          showWalkScore={showWalkScore}
        />
      </ContentSubSection>

      {roomsSubSection}
    </ContentSectionTemplate>
  );
}

// ============================================================================
// Switch
// ============================================================================

export function OverviewSectionSwitch(props: OverviewSectionProps) {
  return (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.STICKY_HEADER_TABS}
      control={<OverviewSectionControl {...props} />}
      variation={<OverviewSectionVariation {...props} />}
    />
  );
}
